import type { Company, CompanyProfileData, CompanyTeamMember } from '@/types';
import { simulate, uid } from '@/services/api/client';
import { withBackend } from '@/services/api/backend';
import { getQuestionsForIndustry } from '@/constants/industryQuestions';

/**
 * Companies. A user can own multiple companies, including parent → subsidiary
 * relationships (`parentCompanyId`). Mock-first via `withBackend`: `list` maps
 * to `listCompanies` and `get` to `getCompanyDashboard` when the backend is on;
 * otherwise the seed below is used. Shapes match the Firestore `companies`
 * collection so a backend swap is drop-in.
 */
const team = (): CompanyTeamMember[] => [
  { id: 'tm1', name: 'Alex Morgan', email: 'alex@demo.example', role: 'owner', department: 'Executive', joinedAt: new Date().toISOString() },
  { id: 'tm2', name: 'Priya Patel', email: 'priya@demo.example', role: 'admin', department: 'People', joinedAt: new Date().toISOString() },
];

let COMPANIES: Company[] = [
  {
    id: 'co_demo', name: 'Demo Industries', industryId: 'ind_ai', legalName: 'Demo Industries Inc.',
    description: 'Parent company seeded for the Endeeko dashboard preview.', websiteUrl: 'https://demo.example',
    foundedYear: 2022, employeeCount: 42, membershipTier: 'pro',
    branding: { primaryColor: '#2563eb', secondaryColor: '#0f172a', tagline: 'Operate. Hire. Grow.' },
    locations: [{ id: 'loc_hq', label: 'HQ', addressLine1: '500 Market St', city: 'San Francisco', country: 'US', isHeadquarters: true }],
    team: team(), createdAt: new Date().toISOString(),
    parentCompanyId: null, status: 'active', planName: 'Growth', tokens: 1850,
    ownerId: 'me', industryName: 'AI Software Development', naicsCode: '541511', onboardingCompleted: true,
  },
  {
    id: 'co_demo_labs', name: 'Demo Labs', industryId: 'ind_rnd',
    description: 'R&D subsidiary of Demo Industries.', foundedYear: 2023, employeeCount: 11, membershipTier: 'free',
    branding: { primaryColor: '#7c3aed', secondaryColor: '#0f172a' },
    locations: [], team: [], createdAt: new Date().toISOString(),
    parentCompanyId: 'co_demo', status: 'trial', planName: 'Free trial', tokens: 240,
    ownerId: 'me', industryName: 'Professional, Scientific, and Technical Services', naicsCode: '541700', onboardingCompleted: false,
  },
  {
    id: 'co_harbor', name: 'Harborlight Hospitality', industryId: 'ind_hospitality',
    description: 'Standalone hospitality company.', foundedYear: 2019, employeeCount: 80, membershipTier: 'enterprise',
    branding: { primaryColor: '#0891b2', secondaryColor: '#0f172a' },
    locations: [], team: [], createdAt: new Date().toISOString(),
    parentCompanyId: null, status: 'active', planName: 'Enterprise', tokens: 5200,
    ownerId: 'me', industryName: 'Accommodation and Food Services', naicsCode: '721110', onboardingCompleted: true,
  },
];

/** Build the in-app dashboard URL for a company. */
export const getCompanyDashboardUrl = (companyId: string) => `/industry/company/${companyId}`;

export const companyService = {
  /**
   * All companies the caller owns / belongs to. PRODUCTION-ONLY: backed by the
   * real `listCompanies` callable; on the backend being unavailable it returns
   * an empty list (never the demo companies), so the industry & tokens views
   * show real data — a caller with no companies genuinely sees none.
   */
  list: () => withBackend<Company[]>('listCompanies', {}, () => simulate([])),

  /** Companies owned by the caller (backend filters by the auth uid). */
  getUserCompanies: (userId: string) =>
    withBackend<Company[]>('listCompanies', { ownerId: userId }, () => simulate([])),

  getById: (id: string) =>
    withBackend<Company | null>('getCompany', { companyId: id }, () => simulate(COMPANIES.find((c) => c.id === id) ?? null)),

  /** Industry-specific onboarding questions (mock map; future Firestore collection). */
  getOnboardingQuestions: (industryId: string) =>
    withBackend('getOnboardingQuestions', { industryId }, () => simulate(getQuestionsForIndustry(industryId))),

  /** Save the dynamic company profile; optionally mark onboarding complete. */
  updateProfile: (companyId: string, profile: CompanyProfileData, opts?: { completeOnboarding?: boolean }) => {
    const local = () => {
      COMPANIES = COMPANIES.map((c) =>
        c.id === companyId
          ? { ...c, profile: { ...c.profile, ...profile }, ...(opts?.completeOnboarding ? { onboardingCompleted: true } : {}) }
          : c,
      );
      return simulate(COMPANIES.find((c) => c.id === companyId) ?? null);
    };
    return withBackend<Company | null>('updateCompanyProfile', { companyId, profile, ...opts }, local);
  },

  /** Alias matching the spec's naming. */
  getCompany: (id: string) =>
    withBackend<Company | null>('getCompany', { companyId: id }, () => simulate(COMPANIES.find((c) => c.id === id) ?? null)),

  getCompanyDashboardUrl,

  /** Subsidiaries of a parent company. */
  subsidiaries: (parentId: string) => COMPANIES.filter((c) => c.parentCompanyId === parentId),

  // Connected to endeeko `getCompanyDashboard` callable; mock-first. Returns the
  // primary (first) company for back-compat with single-company consumers.
  get: () => withBackend<Company | null>('getCompanyDashboard', {}, () => simulate(COMPANIES[0] ?? null)),

  update: (patch: Partial<Company>) => {
    const local = () => {
      const id = patch.id ?? COMPANIES[0]?.id;
      COMPANIES = COMPANIES.map((c) => (c.id === id ? { ...c, ...patch } : c));
      return simulate(COMPANIES.find((c) => c.id === id) ?? null);
    };
    // The endeeko-updateCompanyProfile callable expects { companyId, updates },
    // not the patch fields flattened at the top level — sending patch directly
    // (as this used to) always 400'd ("Invalid input") against the real backend.
    const { id, ...updates } = patch;
    const companyId = id ?? COMPANIES[0]?.id;
    return withBackend<Company | null>('updateCompanyProfile', { companyId, updates }, local);
  },

  // Team management operates on the primary company (back-compat single-arg API).
  inviteTeamMember: (input: Omit<CompanyTeamMember, 'id' | 'joinedAt'>) => {
    const local = () => {
      const member: CompanyTeamMember = { ...input, id: uid('tm'), joinedAt: new Date().toISOString() };
      const id = COMPANIES[0]?.id;
      COMPANIES = COMPANIES.map((c) => (c.id === id ? { ...c, team: [...c.team, member] } : c));
      return simulate(member);
    };
    return withBackend<CompanyTeamMember>('manageExecutives', { action: 'add', member: input }, local);
  },

  removeTeamMember: (id: string) => {
    const local = () => {
      const cid = COMPANIES[0]?.id;
      COMPANIES = COMPANIES.map((c) => (c.id === cid ? { ...c, team: c.team.filter((m) => m.id !== id) } : c));
      return simulate(true);
    };
    return withBackend<boolean>('manageExecutives', { action: 'remove', id }, local);
  },

  /** Account groups: mark `companyId` as a subsidiary of `parentCompanyId` (caller must own both). */
  setParentCompany: (companyId: string, parentCompanyId: string) => {
    const local = () => {
      COMPANIES = COMPANIES.map((c) => (c.id === companyId ? { ...c, parentCompanyId } : c));
      return simulate({ companyId, parentCompanyId });
    };
    return withBackend<{ companyId: string; parentCompanyId: string }>('setParentCompany', { companyId, parentCompanyId }, local);
  },

  /** Account groups: make `companyId` a top-level company again. */
  removeParentCompany: (companyId: string) => {
    const local = () => {
      COMPANIES = COMPANIES.map((c) => (c.id === companyId ? { ...c, parentCompanyId: null } : c));
      return simulate({ companyId });
    };
    return withBackend<{ companyId: string }>('removeParentCompany', { companyId }, local);
  },
};
